// ReviewSubmitScreen.jsx — dynamic sections from mockData via buildReviewSections
import React from 'react';
import styles from '../../styles/assessments.module.css';
import { buildReviewSections, getAssessmentMeta } from '../../utils/scoreUtils';
import {  ChevronLeft, ChevronRight, ScreenHeader } from './SharedUI';

// Scroll-to-bottom hook (inline — removes external dep)
import { useRef, useState, useEffect, useCallback } from 'react';

const useScrollToBottom = () => {
  const scrollRef = useRef(null);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowButton(distFromBottom > 80);
    };
    el.addEventListener('scroll', onScroll);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, []);

  return { scrollRef, showButton, scrollToBottom };
};

// ─── Assessment type badge icons ─────────────────────────
const TYPE_LABELS = {
  menopause: '🌸 Menopause',
  pcos: '🔬 PCOS',
  pregnancy: '🤰 Pregnancy',
  menstrual: '📅 Menstrual Disorder',
  mental: '🧠 Mental Health',
  anxiety: '😟 Anxiety',
  fertility: '🌱 Fertility',
  thyroid: '🦋 Thyroid',
  lifestyle: '🏃 Lifestyle Risk',
  hormonal: '⚗️ Hormonal',
  diabetes: '💉 Diabetes Risk',
  bmi: '⚖️ BMI & Metabolic',
  sleep: '🌙 Sleep Quality',
  stress: '😰 Stress',
  social: '🤝 Social Determinants',
  general: '🩺 General Health',
};

// ─── Scroll-to-bottom button ──────────────────────────────
const ScrollBtn = ({ onClick }) => (
  <button className={styles.scrollToBottomBtn} onClick={onClick} aria-label="Scroll to bottom">
    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────

const ReviewSubmitScreen = ({
  assessmentType,
  allAnswers = {},
  onBack,
  onEdit,
  onSubmit,
    customSections,   
  onExit,
}) => {
  const { scrollRef, showButton, scrollToBottom } = useScrollToBottom();

  // Build sections dynamically from mockData
  // const sections = buildReviewSections(assessmentType);
   const sections = customSections ?? buildReviewSections(assessmentType);
  // const meta = getAssessmentMeta(assessmentType) ?? { color: '#7c3aed', icon: '🩺', label: 'PCOS Risk' };
  const meta = getAssessmentMeta(assessmentType);

  return (
    <div className={styles.reviewWrapper}>

      {/* ── Top bar ── */}
      {/* <div className={styles.reviewTopBar}> */}
      {/* <button className={styles.backBtn} onClick={onBack} aria-label="Back">
          <ChevronLeft />
        </button> */}
      {/* <span className={styles.reviewTopBarTitle}>Review &amp; Submit</span> */}
      {/*         
        <button className={styles.exitBtn} onClick={onExit}>Save &amp; Exit</button> */}
      <ScreenHeader onExit={onExit} />

      {/* </div> */}

      {/* ── Progress — 100% ── */}
      <div className={styles.progressWrap}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: '100%' }} />
        </div>
        <div className={styles.progressMeta}>
          <span className={styles.progressStep}>All steps complete</span>
          <span className={styles.stepPct}>100%</span>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div
        className={` ${styles.reviewScrollBody} ${styles.reviewBody}`}
        ref={scrollRef}
      >
        <h2 className={styles.screenTitle}>Review &amp; Submit</h2>
        <p className={styles.screenSubtitle}>
          Please review your information before submitting. You can edit any section.
        </p>

        {/* Assessment badge */}
        <div
          className={styles.reviewBadge}
          style={{ borderColor: meta.color + '40', background: meta.color + '10' }}
        >
          <span style={{ color: meta.color, fontWeight: 700 }}>
            {TYPE_LABELS[assessmentType] || `${meta.icon} ${meta.label}`}
          </span>
          <span className={styles.reviewBadgeSub}>Assessment</span>
        </div>


        <div className={styles.reviewSummaryRow}>
          <div className={styles.reviewSummaryCard}>
            <div className={styles.reviewSummaryNum} style={{ color: meta.color }}>
              {sections.length}
            </div>
            <div className={styles.reviewSummaryLabel}>Sections</div>
          </div>
          <div className={styles.reviewSummaryCard}>
            <div className={styles.reviewSummaryNum} style={{ color: '#059669' }}>
              {sections.length}
            </div>
            <div className={styles.reviewSummaryLabel}>Completed</div>
          </div>
          <div className={styles.reviewSummaryCard}>
            <div className={styles.reviewSummaryNum} style={{ color: '#2563EB' }}>
              100%
            </div>
            <div className={styles.reviewSummaryLabel}>Progress</div>
          </div>
        </div>

        {/* Section list */}
        <div className={styles.reviewSectionList}>
          {sections.map((section, idx) => (
            <div key={section.step} className={styles.reviewSectionRow}>
              {/* Left — number + check */}
              <div className={styles.reviewSectionLeft}>
                <div className={styles.reviewSectionCheck}>
                  <svg width="12" height="12" fill="none" stroke="white" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className={styles.reviewSectionInfo}>
                  <span className={styles.reviewSectionNum}>Step {idx + 1}</span>
                  <span className={styles.reviewSectionLabel}>{section.label}</span>
                </div>
              </div>

              {/* Right — edit button */}
              <button
                className={styles.reviewEditBtn}
                onClick={() => onEdit(section.step)}
                style={{ color: meta.color }}
              >
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                </svg>
                Edit
              </button>
            </div>
          ))}
        </div>

        {/* Confirmation notice */}
        <div className={styles.reviewNotice}>
          <svg width="16" height="16" fill="none" stroke="#5B21B6" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          <p className={styles.reviewNoticeText}>
            <strong>Your information is secure and confidential.</strong>
            {' '}By submitting, you confirm that all information provided is accurate to the best of your knowledge.
          </p>
        </div>

        {/* Extra bottom padding so footer doesn't obscure content */}
        <div style={{ height: 20 }} />
      </div>

      {/* Scroll-to-bottom fab */}
      {showButton && <ScrollBtn onClick={scrollToBottom} />}

      {/* ── Footer nav ── */}
      <div className={styles.reviewFooterNavFixed}>
        <button className={styles.navBack} onClick={onBack}>
          <ChevronLeft /> Back
        </button>
        <button className={styles.navNext} onClick={onSubmit}>
          Submit Assessment <ChevronRight />
        </button>
      </div>
    </div>
  );
};

export default ReviewSubmitScreen;
